/* eslint-disable */
import React, { useEffect, useState, useMemo } from "react";
import { Spin, message, Divider, Tabs, Select } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import EnhancedTable from "@/hocComponents/EnhancedTable";
import http from "@/utils/http";
import api from "@/utils/api";
import { bankRecDate as convertArrayToDayjs } from "@/views/BankReconciliation/BankRec/dates";

export default function UnreconciledPage() {
  const [searchParams] = useSearchParams();
  const storageKey = searchParams.get("key");
  const storedData = useMemo(() => {
    if (!storageKey) return null;
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [storageKey]);

  const [loading, setLoading] = useState(false);
  const [bankTransList, setBankTransList] = useState([]);
  const [selectedRightIds, setSelectedRightIds] = useState([]);
  const [bankFilters, setBankFilters] = useState({});
  // Tab state 
  const [activeTab, setActiveTab] = useState("CR");
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 500,
    total: 0,
  });

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setBankFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleFilterSubmit = (filters) => {
    setBankFilters(filters);
    setPagination(prev => ({ ...prev, current: 1 }));
    loadBankTransactions(1, pagination.pageSize, filters);
  };

  const handleTableChange = (paginationConfig, filters, sorter) => {
    const current = paginationConfig.current || 1;
    const pageSize = paginationConfig.pageSize || 500;
    setPagination({
      current: current,
      pageSize: pageSize,
      total: pagination.total,
    });

    const currentFilters = { ...bankFilters };
    if (filters) {
      Object.keys(filters).forEach(key => {
        if (filters[key] && filters[key].length > 0) {
          currentFilters[key] = filters[key][0];
        }
      });
    }
    
    loadBankTransactions(current, pageSize, currentFilters);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setPagination(prev => ({ ...prev, current: 1 }));
    setBankFilters({});
    loadBankTransactions(1, 500, {}, key);
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setPagination(prev => ({ ...prev, current: 1 }));
    setBankFilters({});
    loadBankTransactions(1, 500, {}, null, year);
  };

  const loadBankTransactions = async (page = 1, count = 500, filters = {}, journalType = null, year = null) => {
    setLoading(true);
    try {
      const journal = journalType || activeTab;
      const currentYear = year || selectedYear;
      const requestData = {
        page: page,
        count: count,
        year: currentYear,
        journal: journal, 
      };

      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
          if (key === 'date' && filters[key]) {
            const date = dayjs(filters[key]);
            if (date.isValid()) {
             requestData[key] = date.format("YYYY-MM-DD");
            }
          } else {
            requestData[key] = filters[key];
          }
        }
      });
      const response = await http.post(api.getShowUnreconciled, requestData);
      if (response && response.code === "1") {
        const dataList = response.data?.list || [];
        const total = response.data?.total || 0;
        setBankTransList(dataList);
        setPagination({
          current: response.data?.pageNum || page,
          pageSize: response.data?.pageSize || count,
          total: total,
        });
      } else {
        message.error(response?.msg);
      }
    } catch (error) {
      message.error("Failed to load data, please try again later");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBankTransactions(1, 500);
  }, []);

  const tabItems = [
    {
      key: "CR",
      label: "Bank Credit Not Reconciled",
    },
    {
      key: "CP",
      label: "Bank Debits Not Reconciled",
    },
  ];

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const options = [];
    for (let year = currentYear; year >= currentYear - 20; year--) {
      options.push({
        value: year,
        label: year.toString(),
      });
    }
    return options;
  };

  const bankColumns = [
    { 
      title: "Amount", 
      dataIndex: "amountRow", 
      filterKey: "amount", 
    },
    { 
      title: "Check #", 
      dataIndex: "checkNo", 
      filterKey: "checkNo",
    },
    {
      title: "Date",
      dataIndex: "date",
      filterKey: "date",
      filterType: "date",
      render: (v) => {
        return convertArrayToDayjs(v)?.format("MM/DD/YYYY");
      },
    },
    { 
      title: "Entity", 
      dataIndex: "trust", 
      filterKey: "trust",
    },
    { 
      title: "Bank Name", 
      dataIndex: "bankName", 
      filterKey: "bankName",
    },
    { 
      title: "Account #", 
      dataIndex: "bankAccount", 
      filterKey: "bankAccount",
    },
    { 
      title: "Description", 
      dataIndex: "remark", 
      filterKey: "remark",
    },
    { 
      title: "Bai Code", 
      dataIndex: "baiCode", 
      filterKey: "baiCode",
    },
  ];

  return (
    <div
      style={{
        padding: 20,
        height: "calc(100vh - 40px)", 
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="header">
        <div className="header-title">
          <MenuOutlined style={{ marginRight: 10 }} />
          {activeTab === "CR" 
            ? "BANK CREDIT NOT RECONCILED" 
            : "BANK DEBITS NOT RECONCILED"
          }
        </div>
      </div>

      <Divider style={{ borderColor: "#E2E8F0" }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
          style={{ marginBottom: 0 }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, color: '#000', fontWeight: 500 }}>Year:</span>
          <Select
            value={selectedYear}
            onChange={handleYearChange}
            options={getYearOptions()}
            style={{ width: 120 }}
            placeholder="Select Year"
          />
        </div>
      </div>

      <Spin
        spinning={loading}
        style={{ flex: 1, display: "flex", flexDirection: "column" }}
      >
        <EnhancedTable
          columns={bankColumns}
          dataSource={bankTransList}
          loading={loading}
          rowKey="bankTransNewId"
          filters={bankFilters}
          onFilterChange={handleFilterChange}
          onFilterSubmit={handleFilterSubmit}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            showTotal: (total) => `Total ${total} items`,
          }}
          onChange={handleTableChange}
          scroll={{ y: "calc(100vh - 350px)" }}
        />
      </Spin>
    </div>
  );
}